import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { WaterSample } from "@/services/backend/samples/types/water-sample";
import { Filter } from "@/shared/lib/filter/filter";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesWaterApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesWaterSample"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      samplesWaterSamplePut: build.mutation<WaterSample, PutRequest>({
        query: (request) => ({
          url: `/samples/water`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesWaterSample", id: request.id },
          "SamplesWaterSample",
          "SamplesSampleViewModel",
        ],
      }),
      samplesWaterSampleUpdateLab: build.mutation<
        WaterSample,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/water/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesWaterSample", id: request.id },
          "SamplesWaterSample",
          "SamplesSampleViewModel",
        ],
      }),
      samplesWaterSampleShow: build.query<WaterSample, ShowRequest>({
        query: (request) => ({
          url: `/samples/water`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("SamplesWaterSample"),
      }),
      samplesWaterSampleList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/water/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.samples, "SamplesWaterSample"),
      }),
      samplesWaterSampleDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/samples/water`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["SamplesWaterSample", "SamplesSampleViewModel"],
      }),
    }),
  });

export const {
  useSamplesWaterSamplePutMutation,
  useSamplesWaterSampleUpdateLabMutation,
  useSamplesWaterSampleShowQuery,
  useSamplesWaterSampleListQuery,
  useLazySamplesWaterSampleListQuery,
  useSamplesWaterSampleDeleteMutation,
} = samplesWaterApi;

export type PutRequest = WaterSample;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  samples: WaterSample[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached water sample for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateWaterSampleCache = updateCache(
  samplesWaterApi,
  "samplesWaterSampleShow",
);
